import { useState, type FormEvent } from 'react'
import { CButton, CContainer, CFormInput, CFormSelect } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilCloudDownload, cilSearch } from '@coreui/icons'
import { useQuery } from '@tanstack/react-query'
import { publicApi, apiErrorMessage } from '../../api/client'
import { ErrorState, LoadingState } from '../../components/common/AsyncState'
import { LedgerTimeline } from '../../components/public/LedgerTimeline'
import { auditCategories } from '@larice/contracts'

export function LedgerPage() {
  const [form, setForm] = useState({ search: '', category: '', project: '' })
  const [filters, setFilters] = useState(form)
  const [page, setPage] = useState(1)
  const query = useQuery({
    queryKey: ['public', 'ledger', filters, page],
    queryFn: () => publicApi.ledger({ ...filters, page, pageSize: 20 }),
    placeholderData: (previous) => previous,
  })
  const submit = (event: FormEvent) => {
    event.preventDefault()
    setPage(1)
    setFilters(form)
  }
  const params = new URLSearchParams(Object.entries(filters).filter(([, value]) => value))
  return (
    <>
      <section className="page-hero page-hero--ledger">
        <CContainer xxl>
          <span className="eyebrow">LARICE Compute Ledger</span>
          <h1>A public timeline that can be followed, filtered and verified.</h1>
          <p>
            Published records are append-only and hash-chained. Corrections appear as new events linked
            to the original record.
          </p>
        </CContainer>
      </section>
      <section className="section-space--tight">
        <CContainer xxl>
          <form className="ledger-filter" onSubmit={submit}>
            <div className="ledger-filter__search">
              <CIcon icon={cilSearch} />
              <CFormInput
                aria-label="Search ledger"
                placeholder="Search record ID, title, summary or project…"
                value={form.search}
                onChange={(event) => setForm({ ...form, search: event.target.value })}
              />
            </div>
            <CFormSelect
              aria-label="Filter category"
              value={form.category}
              onChange={(event) => setForm({ ...form, category: event.target.value })}
            >
              <option value="">All event categories</option>
              {auditCategories.map((item) => (
                <option key={item} value={item}>
                  {item[0]?.toUpperCase()}
                  {item.slice(1)}
                </option>
              ))}
            </CFormSelect>
            <CFormInput
              aria-label="Filter project"
              placeholder="Project alias"
              value={form.project}
              onChange={(event) => setForm({ ...form, project: event.target.value })}
            />
            <CButton color="primary" type="submit">
              Apply filters
            </CButton>
          </form>
          <div className="ledger-tools">
            <div>
              <strong>{query.data?.total ?? 0}</strong>
              <span>published records</span>
            </div>
            <div>
              <a className="btn btn-light" href={`/api/v1/public/ledger/export.csv?${params}`}>
                <CIcon icon={cilCloudDownload} /> CSV
              </a>
              <a className="btn btn-light" href={`/api/v1/public/ledger/export.json?${params}`}>
                <CIcon icon={cilCloudDownload} /> JSON
              </a>
            </div>
          </div>
          {query.isLoading && <LoadingState />}
          {query.isError && (
            <ErrorState message={apiErrorMessage(query.error)} retry={() => void query.refetch()} />
          )}
          {query.data && (
            <>
              <LedgerTimeline events={query.data.items} />
              <div className="pagination-row">
                <CButton
                  color="light"
                  disabled={page <= 1}
                  onClick={() => setPage((value) => value - 1)}
                >
                  Previous
                </CButton>
                <span>
                  Page {query.data.page} of {Math.max(1, query.data.totalPages)}
                </span>
                <CButton
                  color="light"
                  disabled={page >= query.data.totalPages}
                  onClick={() => setPage((value) => value + 1)}
                >
                  Next
                </CButton>
              </div>
            </>
          )}
        </CContainer>
      </section>
    </>
  )
}
